import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import type { PaginationState } from '~/modules/pagination/type';
import {
    pageStartsForHydratedPages,
    useTabLoadMore,
} from './use-tab-load-more';

interface UseProfessionalTabViewMoreParams<T extends TileableModel> {
    /** Tiles for the loader-served page (`pagination.page`). */
    initialTiles: TileViewModel<T>[];
    pagination: PaginationState;
    urlSegment: string;
    searchType: 'project' | 'product';
}

interface UseProfessionalTabViewMoreReturn<T extends TileableModel> {
    /** Initial tiles plus every appended page. */
    tiles: TileViewModel<T>[];
    hasMore: boolean;
    isLoading: boolean;
    loadMore: () => void;
    /** Tile index → the page starting there (viewport page markers). */
    pageStartByIndex: Record<number, number>;
}

/**
 * Infinite-scroll pagination for the professional Projects / Products tabs — a
 * thin {@link useTabLoadMore} wrapper that keys dedup off the tile's model id.
 */
export function useProfessionalTabViewMore<T extends TileableModel>({
    initialTiles,
    pagination,
    urlSegment,
    searchType,
}: UseProfessionalTabViewMoreParams<T>): UseProfessionalTabViewMoreReturn<T> {
    const { items, hasMore, isLoading, loadMore, pageStartByIndex } =
        useTabLoadMore<TileViewModel<T>>({
            initialItems: initialTiles,
            initialHasMore: pagination.page < pagination.totalPages,
            initialPage: pagination.page,
            urlSegment,
            searchType,
            getKey: (tile) => tile.model.id,
            // A ?page=N deep link hydrates pages 1..N in one loader query
            // (pagination.page = N), so the initial tiles span N pages —
            // each needs its own start boundary or the whole prefix would
            // be labelled page N.
            initialPageStartByIndex: pageStartsForHydratedPages(
                pagination.page,
                pagination.pageSize,
            ),
        });

    return { tiles: items, hasMore, isLoading, loadMore, pageStartByIndex };
}
